import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box, Typography, Paper, IconButton, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Drawer, Divider, Chip, CircularProgress, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Switch
} from "@mui/material";

// Icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';

import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';

import { useAuth } from "../../contexts/AuthContext";
import AppPagination from "../../components/base/AppPagination";
import { usePagination } from "../../hooks/usePagination";
import AppSearch from "../../components/base/AppSearch";

const Newsletter = () => {
    const { user } = useAuth();
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSub, setSelectedSub] = useState(null);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const [openDialog, setOpenDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [emailInput, setEmailInput] = useState("");

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");

    // --- 1. CHARGEMENT DES DONNÉES ---
    const fetchSubscribers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiUrl}/admin/subscribe/l/a/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSubscribers(response.data.result || []);
        } catch (err) {
            toast.error("Erreur lors du chargement des abonnés");
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token]);

    useEffect(() => {
        if (user) fetchSubscribers();
    }, [user, fetchSubscribers]);

    // --- 2. UPDATE (Email ou Statut) ---
    const handleUpdateSubscriber = async (id, dataToUpdate) => {
        try {
            setSubmitting(true);
            await axios.patch(`${apiUrl}/admin/subscribe/u/update/${id}`, 
                dataToUpdate,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Mise à jour réussie");
            fetchSubscribers();
        } catch (err) {
            toast.error(err.response?.data?.message || "Erreur lors de la mise à jour");
        } finally {
            setSubmitting(false);
        }
    };

    // --- 3. AJOUTER UN ABONNÉ ---
    const handleAddSubscriber = async () => {
        if (!emailInput) return toast.warning("Veuillez saisir un email");
        try {
            setSubmitting(true);
            await axios.post(`${apiUrl}/admin/subscribe`, { email: emailInput }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Abonné ajouté");
            setOpenDialog(false);
            setEmailInput("");
            fetchSubscribers();
        } catch (err) {
            toast.error(err.response?.data?.message || "Erreur lors de l'ajout");
        } finally {
            setSubmitting(false);
        }
    };

    // --- 4. SUPPRIMER UN ABONNÉ ---
    const handleDeleteSubscriber = async (id) => {
        if (window.confirm("Supprimer cet abonné ?")) {
            try {
                await axios.delete(`${apiUrl}/admin/subscribe/d/delete/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Abonné supprimé");
                setOpenDrawer(false);
                fetchSubscribers();
            } catch (err) {
                toast.error("Erreur lors de la suppression");
            }
        }
    };

    // --- 5. EXPORT EXCEL ---
    const handleExportExcel = () => {
        if (subscribers.length === 0) return toast.info("Aucune donnée");
        const data = subscribers.map(sub => ({
            ID: sub.id,
            Email: sub.email,
            Statut: sub.abonner_nwes === 1 ? "Actif" : "Inactif",
            Date: new Date(sub.created_at).toLocaleDateString('fr-FR')
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Abonnés");
        XLSX.writeFile(wb, "Newsletter_Abonnes.xlsx");
    };

    // --- 6. FILTRAGE & PAGINATION ---
    const filteredSubscribers = useMemo(() => {
        return subscribers.filter((s) => s.email?.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [subscribers, searchTerm]);

    const itemsPerPage = 10;
    const { next, currentData, currentPage, count, totalItems } = usePagination(filteredSubscribers, itemsPerPage);

    return (
        <Box sx={{ pt: { xs: "80px", md: "20px" }, px: 3, pb: 4 }}>
            {/* Header */}
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" sx={{ mb: 4 }} spacing={2}>
                <Box>
                    <Typography variant="h3" sx={{ fontWeight: "800", color: "#2B3674", fontSize: "34px" }}>Newsletter</Typography>
                    <Typography sx={{ color: "#A3AED0", fontWeight: 500 }}>Gestion des abonnés</Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <AppSearch value={searchTerm} onChange={setSearchTerm} />
                    <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportExcel} sx={{ borderRadius: "20px", color: "#05CD99", borderColor: "#05CD99", px: 8,               // Réduit le padding (12 était trop large)
                            py: 1.5,      }}>Excel</Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)} sx={{
                            bgcolor: "#4318FF",
                            borderRadius: "20px", // Plus arrondi pour l'effet "pilule"
                            px: 8,               // Réduit le padding (12 était trop large)
                            py: 1.5,             // Ajuste le padding vertical au lieu d'une hauteur fixe
                            textTransform: 'none',
                            fontSize: '14px',    // Taille de police standard dashboard
                            fontWeight: 600,     // Pour plus de lisibilité
                            whiteSpace: 'nowrap', // Force le texte sur une seule ligne
                            boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)', // Optionnel: petite ombre portée
                            "&:hover": {
                                bgcolor: "#3311CC",
                                boxShadow: 'none'
                            }
                        }}                 >Ajouter</Button>
                </Stack>
            </Stack>

            {/* Tableau */}
            <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #F4F7FE' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#F4F7FE' }}>
                            <TableRow>
                                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>EMAIL</TableCell>
                                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>DATE D'INSCRIPTION</TableCell>
                                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>STATUT</TableCell>
                                <TableCell align="right" sx={{ color: '#A3AED0', fontWeight: 700 }}>ACTIONS</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={30} sx={{ my: 4, color: '#4318FF' }} /></TableCell></TableRow>
                            ) : (
                                currentData().map((sub) => (
                                    <TableRow key={sub.id} hover>
                                        <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>{sub.email}</TableCell>
                                        <TableCell sx={{ color: '#4318FF', fontWeight: 700 }}>
                                            {new Date(sub.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Switch
                                                    size="small"
                                                    checked={sub.abonner_nwes === 1}
                                                    onChange={() => handleUpdateSubscriber(sub.id, { abonner_nwes: sub.abonner_nwes === 1 ? 0 : 1 })}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#05CD99' },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#05CD99' }
                                                    }}
                                                />
                                                <Chip
                                                    label={sub.abonner_nwes === 1 ? "Actif" : "Inactif"}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: sub.abonner_nwes === 1 ? '#E6F9F0' : '#FFF5F5',
                                                        color: sub.abonner_nwes === 1 ? '#05CD99' : '#EE5D50',
                                                        fontWeight: 700
                                                    }}
                                                />
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <IconButton onClick={() => { setSelectedSub(sub); setOpenDrawer(true); }} sx={{ color: '#4318FF', bgcolor: '#F4F7FE' }}><VisibilityIcon fontSize="small" /></IconButton>
                                                <IconButton onClick={() => handleDeleteSubscriber(sub.id)} sx={{ color: '#EE5D50', bgcolor: '#FFF5F5' }}><DeleteIcon fontSize="small" /></IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <AppPagination count={count} page={currentPage} onChange={next} totalItems={totalItems} itemsPerPage={itemsPerPage} />
            </Paper>

            {/* Dialog Ajouter */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, color: "#2B3674" }}>Ajouter un abonné</DialogTitle>
                <DialogContent>
                    <TextField autoFocus margin="dense" label="Email" fullWidth variant="outlined" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} sx={{ mt: 1 }} />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
                    <Button onClick={handleAddSubscriber} variant="contained" disabled={submitting} sx={{ bgcolor: "#4318FF" }}>{submitting ? "Ajout..." : "Confirmer"}</Button>
                </DialogActions>
            </Dialog>

            {/* Drawer Détails & Edit */}
            <Drawer
                anchor="right"
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
                sx={{ zIndex: 3000 }}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 400 },
                        borderRadius: { xs: 0, sm: "20px 0 0 20px" },
                        boxShadow: "-10px 0px 30px rgba(0, 0, 0, 0.05)"
                    }
                }}
            >
                <Box sx={{ p: 4 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#2B3674" }}>Fiche Abonné</Typography>
                        <IconButton onClick={() => setOpenDrawer(false)} sx={{ bgcolor: "#F4F7FE" }}><CloseIcon /></IconButton>
                    </Stack>

                    <Divider sx={{ mb: 4 }} />

                    {selectedSub && (
                        <Stack spacing={4}>
                            <Stack alignItems="center" spacing={2}>
                                <Box sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "#F4F7FE", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #4318FF" }}>
                                    <Typography variant="h4" sx={{ color: "#4318FF", fontWeight: 700 }}>
                                        {selectedSub.email?.charAt(0).toUpperCase()}
                                    </Typography>
                                </Box>
                                
                                <TextField
                                    fullWidth
                                    label="Email de l'abonné"
                                    value={selectedSub.email}
                                    onChange={(e) => setSelectedSub({ ...selectedSub, email: e.target.value })}
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                />
                                
                                <Chip 
                                    label={selectedSub.abonner_nwes === 1 ? "Abonnement Actif" : "Désabonné"} 
                                    sx={{ bgcolor: selectedSub.abonner_nwes === 1 ? "#E6F9F0" : "#FFF5F5", color: selectedSub.abonner_nwes === 1 ? "#05CD99" : "#EE5D50", fontWeight: 700 }} 
                                />
                            </Stack>

                            <Stack spacing={3} sx={{ bgcolor: "#F4F7FE", p: 3, borderRadius: "20px" }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: "#A3AED0", fontWeight: 600 }}>ID UNIQUE</Typography>
                                    <Typography sx={{ color: "#2B3674", fontWeight: 600 }}>#{selectedSub.id}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: "#A3AED0", fontWeight: 600 }}>INSCRIT LE</Typography>
                                    <Typography sx={{ color: "#2B3674", fontWeight: 600 }}>
                                        {new Date(selectedSub.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack spacing={2}>
                                <Button 
                                    fullWidth 
                                    variant="contained" 
                                    startIcon={<SaveIcon />}
                                    onClick={() => handleUpdateSubscriber(selectedSub.id, { email: selectedSub.email })}
                                    disabled={submitting}
                                    sx={{ bgcolor: "#4318FF", borderRadius: "12px", py: 1.5, fontWeight: 700 }}
                                >
                                    {submitting ? "Enregistrement..." : "Sauvegarder"}
                                </Button>

                                <Button 
                                    fullWidth 
                                    onClick={() => handleDeleteSubscriber(selectedSub.id)}
                                    startIcon={<DeleteIcon />}
                                    sx={{ color: "#EE5D50", fontWeight: 700 }}
                                >
                                    Supprimer
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Drawer>
        </Box>
    );
};

export default Newsletter;